import React, { FC, useEffect } from 'react';
import { get, gt, isEmpty, isNil, replace, set, toNumber } from 'lodash';
import { ProductCardGenericLabels } from './ProductCard';
import { Dropdown } from '../Dropdown';
import { TextInput } from '../TextInput';
import { PLAN_PARAMS, SUPPORTED_LOCALES, DEFAULT_TRIP_COST_RULES } from '@trip-ux/constants';
import { COVERME_TRAVELLER_TYPES, isAgency, ReduxStore } from '@trip-ux/redux-store';
import { invalidCalculatedTopUpEndDate } from './card.utils';
import { formatCurrency, interpolateTemplate } from '@trip-ux/common';
import { useSelector } from 'react-redux';

const removeNonNumericValue = RegExp('\\D', 'g');

export interface ProductCardDropdown {
  label: string;
  value: string;
}

export interface ProductCardAttributesProps {
  deductibles?: ProductCardDropdown[];
  tripDays?: ProductCardDropdown[];
  coverageAmount?: ProductCardDropdown[];
  sumInsuredAfterNeeded?: boolean;
  tripCost?: boolean;
  tripCostMaxAmt?: number;
  paxTripCostValue?: string;
  hasCoverageAmount?: boolean;
  genericLabels: ProductCardGenericLabels;
  onAction: (type: string, value: string) => void;
  id: string;
  values: {
    deductible?: string;
    sumInsuredAfter?: string;
    daysPerTrip?: string;
    tripCost?: string;
    coverageAmount?: string;
  };
  isMQtaken?: boolean;
  disabledOnCalculate?: boolean;
  travellerType?: string;
  tripDaysMaxTopUp?: number;
  tripDaysMinTopUp?: number;
  tripStartDate?: string;
  applicationDate?: string;
  dateFormat?: string;
  tooltips?: {
    deductible: string;
    tripCost: string;
    tripDuration: string;
    coverageAmount: string;
  };
  language?: SUPPORTED_LOCALES;
  setEnableRefreshButton?: (enable: boolean) => void;
  code?: string;
}

const coverageAmountRequired = (coverageAmountList, coverageAmountValue): boolean => {
  return !isEmpty(coverageAmountList) && isEmpty(coverageAmountValue);
};

const TripDaysWithTopUp: FC<ProductCardAttributesProps> = ({
  genericLabels,
  onAction,
  values,
  id,
  travellerType,
  tripDaysMaxTopUp,
  tripDaysMinTopUp,
  disabledOnCalculate,
  tripStartDate,
  applicationDate,
  dateFormat
}) => {
  return (
    travellerType === COVERME_TRAVELLER_TYPES.EXISTING_PLAN && (
      <TextInput
        name="tripDays"
        type="text"
        maxLength={3}
        value={values.daysPerTrip}
        className="product-card-attrs"
        onChange={e => {
          const value = toNumber(replace(e.target.value, removeNonNumericValue, ''));
          onAction(PLAN_PARAMS.TRIP_DAYS, value === 0 ? '' : value.toString());
        }}
        id={`${id}-trip-days`}
        error={
          (gt(toNumber(values.daysPerTrip) + tripDaysMinTopUp, tripDaysMaxTopUp) &&
            interpolateTemplate(genericLabels.tripDaysMaxTopUpError, { existingTripDaysCovered: tripDaysMinTopUp })) ||
          (invalidCalculatedTopUpEndDate(
            tripStartDate,
            applicationDate,
            dateFormat,
            tripDaysMinTopUp,
            toNumber(values.daysPerTrip)
          ) &&
            genericLabels.topUpEndDate)
        }
        disabled={disabledOnCalculate}>
        {genericLabels.tripDaysWithTopUpLabel}
      </TextInput>
    )
  );
};

const disabledFieldsForCoverage = (disabledConditions, values, coverageAmount) => {
  return disabledConditions || (values && coverageAmountRequired(coverageAmount, values.coverageAmount));
};

const isDisabled = (isMQtaken, disabledOnCalculate): boolean => {
  return !isMQtaken || disabledOnCalculate;
};

const checkPlanRuleViolation = (
  orgCategory,
  planRule,
  insuredAge,
  tripCostValue
): boolean => {
  // Early exit for agency organizations
  if (isAgency(orgCategory)) {return false;}

  // Early exit if no plan rule exists
  if (!planRule) {return false;}

  // Check both age and value requirements
  const meetsAgeRequirement = insuredAge >= planRule.minAge;
  const belowMinimumValue = tripCostValue < planRule.minValue;

  return meetsAgeRequirement && belowMinimumValue;
};

const conditionalChecksForTripCostError = (
  hasCoverageAmount,
  hasTripCost,
  values,
  tripCostMaxAmt,
  genericLabels,
  language,
) => {


  const tripCostValue = toNumber(values?.tripCost);
  const maxAmount = toNumber(tripCostMaxAmt);
  const shouldCheckMax = tripCostMaxAmt || (!hasCoverageAmount && !hasTripCost);
  const exceedsMax =
    shouldCheckMax && gt(tripCostValue, maxAmount) && !isNil(tripCostMaxAmt);

  if (exceedsMax) {
    return interpolateTemplate(genericLabels.tripCostError, { amt: formatCurrency(maxAmount, language) });
  }

  return '';
};

export const ProductCardAttributes: FC<ProductCardAttributesProps> = ({
  deductibles = [],
  tripDays = [],
  sumInsuredAfterNeeded = false,
  tripCost = false,
  tripCostMaxAmt = 0,
  paxTripCostValue,
  coverageAmount = [],
  genericLabels,
  onAction,
  values,
  id,
  isMQtaken,
  travellerType,
  tripDaysMaxTopUp,
  tripDaysMinTopUp,
  disabledOnCalculate = false,
  hasCoverageAmount = false,
  tripStartDate,
  applicationDate,
  dateFormat,
  tooltips,
  language,
  setEnableRefreshButton,
  code
}) => {
  const { quoteForm, orgCategory, activeTraveller } = useSelector((store: ReduxStore) => ({
    quoteForm: get(store, 'quoteForm.form'),
    orgCategory: get(store, 'userProfile.organizationCategory'),
    activeTraveller: get(store, 'travellerSelection.openAccordion')
  }));

  // SAFE derivation of insuredAge (handles missing activeTraveller or travellers)
  const travellers: any[] = get(quoteForm, 'travellers', []);
  const insuredAge = (() => {
    if (!Array.isArray(travellers) || travellers.length === 0) {
      return 0;
    }
    let index = 0;
    if (activeTraveller && typeof activeTraveller === 'object') {
      const keys = Object.keys(activeTraveller);
      if (keys.length > 0) {
        const parsed = Number(keys[0]);
        if (!isNaN(parsed) && parsed > 0 && travellers[parsed - 1]) {
          index = parsed - 1;
        }
      }
    }
    const age = travellers[index]?.age;
    return Number(age) || 0;
  })();

  const disabledConditions = isDisabled(isMQtaken, disabledOnCalculate);
  const coverageAmountNeeded = disabledFieldsForCoverage(
    disabledConditions,
    values,
    coverageAmount
  );
  const tripCostErrorConditions = conditionalChecksForTripCostError(
    hasCoverageAmount,
    tripCost,
    values,
    tripCostMaxAmt,
    genericLabels,
    language,
  );

  useEffect(() => {
    if (paxTripCostValue) {
      set(values, 'tripCost', paxTripCostValue);
    }
  }, []);

  return (
    <>
      {tripCost && (
        <TextInput
          name="tripCost"
          type="tel"
          mask="99999"
          placeholder={genericLabels.tripCostPlaceholder}
          value={values.tripCost}
          className="product-card-attrs"
          onChange={e => onAction(PLAN_PARAMS.TRIP_COST, e.target.value)}
          id={`${id}-trip-cost`}
          error={tripCostErrorConditions}
          disabled={
            (hasCoverageAmount && tripCost ? false : coverageAmountNeeded) || disabledOnCalculate || paxTripCostValue
          }
          tooltipContent={get(tooltips, 'tripCost')}
          required={true}>
          {genericLabels.tripCostLabel}
        </TextInput>
      )}
      {!isEmpty(coverageAmount) && (
        <Dropdown
          name="coverageAmount"
          options={coverageAmount}
          placeholder={genericLabels.coverageAmtPlaceholder}
          value={values.coverageAmount}
          onChange={val => {
            onAction(PLAN_PARAMS.COVERAGE_AMOUNT, val);
          }}
          id={`${id}-coverage-amount`}
          tooltipContent={get(tooltips, 'coverageAmount')}
          isDisabled={disabledConditions}
          language={language}>
          {id.includes('STCIH') ? genericLabels.stcihAmtLabel : genericLabels.coverageAmtLabel}
        </Dropdown>
      )}
      {!isEmpty(deductibles) && (
        <Dropdown
          name="deductible"
          options={deductibles}
          placeholder={genericLabels.deductiblePlaceholder}
          value={values.deductible}
          onChange={val => onAction(PLAN_PARAMS.DEDUCTIBLE, val)}
          id={`${id}-deductible`}
          isDisabled={disabledConditions}
          tooltipContent={get(tooltips, 'deductible')}
          language={language}>
          {genericLabels.deductibleLabel}
        </Dropdown>
      )}
      {!isEmpty(tripDays) && (
        <Dropdown
          name="tripDays"
          options={tripDays}
          placeholder={genericLabels.tripDaysPlaceholder}
          value={values.daysPerTrip}
          onChange={val => onAction(PLAN_PARAMS.TRIP_DAYS, val)}
          id={`${id}-trip-days`}
          isDisabled={coverageAmountNeeded}
          tooltipContent={get(tooltips, 'tripDuration')}>
          {genericLabels.tripDaysLabel}
        </Dropdown>
      )}
      <TripDaysWithTopUp
        genericLabels={genericLabels}
        onAction={onAction}
        values={values}
        id={id}
        travellerType={travellerType}
        tripDaysMaxTopUp={tripDaysMaxTopUp}
        tripDaysMinTopUp={tripDaysMinTopUp}
        disabledOnCalculate={disabledConditions}
        tripStartDate={tripStartDate}
        applicationDate={applicationDate}
        dateFormat={dateFormat}
      />
      {sumInsuredAfterNeeded && (
        <TextInput
          name="sumInsuredAfter"
          type="tel"
          mask="99999"
          placeholder={genericLabels.sumInsuredAfterPlaceholder}
          value={values.sumInsuredAfter}
          className="product-card-attrs"
          onChange={e => onAction(PLAN_PARAMS.SUM_INSURED_AFTER, e.target.value)}
          id={`${id}-sum-insured`}
          disabled={disabledConditions}>
          {genericLabels.sumInsuredAfterLabel}
        </TextInput>
      )}
    </>
  );
};
